import { cn } from '@theprintbar/shared';
import { useField } from 'remix-validated-form';
import { Spinner } from 'src/components/Spinner';
import { FieldProps, useOutsideForm } from '../index';

interface SelectProps extends FieldProps<HTMLSelectElement> {
	compact?: boolean;
	labelClassName?: string;
	selectClassName?: string;
	loading?: boolean;
}

export function Select({
	children,
	label,
	name,
	outsideForm,
	optional,
	className,
	fieldRef,
	placeholder,
	compact,
	loading,
	labelClassName,
	selectClassName,
	...props
}: SelectProps) {
	const { error, getInputProps } = outsideForm ? useOutsideForm(name) : useField(name);
	return (
		<div className={cn('relative font-normal mb-4 flex w-full items-end', !compact && 'pt-4', className)}>
			<div className="flex relative w-full rounded-md shadow-sm">
				<div className="relative flex w-full">
					<select
						aria-invalid={!!error}
						aria-describedby={`${name}-error`}
						ref={fieldRef}
						{...getInputProps({
							...props,
							id: name,
						})}
						className={cn(
							'bg-transparent rounded-md text-blue text-sm block w-full border-blue focus:ring-blue',
							compact && 'peer placeholder-transparent bg-white pt-4 pb-0.5',
							error && 'border-red-600',
							loading && 'opacity-50 cursor-not-allowed appearance-none bg-none pr-9',
							selectClassName,
						)}
						disabled={loading}
					>
						{placeholder && (
							<option value="" disabled selected hidden>
								{placeholder}
							</option>
						)}
						{children}
					</select>
					{loading && (
						<div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
							<Spinner className="h-4 w-4 text-blue" />
						</div>
					)}
					{label && (
						<label
							htmlFor={name}
							className={cn(
								'flex w-full items-center justify-between text-blue',
								compact
									? 'absolute left-3 top-1 text-xs block peer-placeholder-shown:top-[10px] peer-placeholder-shown:left-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 transition-all peer-focus:top-1 peer-focus:text-gray-700 peer-focus:text-xs peer-focus:left-3'
									: 'absolute left-0.5 -top-5 text-sm',
								labelClassName,
							)}
						>
							{label}
							{optional && <span className="text-xs mx-2 text-grey/80">(optional)</span>}
						</label>
					)}
				</div>

				{error && (
					<span id={`${name}-error`} className="absolute right-0 -top-4 text-xs text-red-600">
						{error}
					</span>
				)}
			</div>
		</div>
	);
}
